import { config } from "./config.js";

// 音。出来事(world.events)を受けて鳴らす(見た目と同じく、試合の結果には関わらない)。
// 素材は SENSEOFVOID から持ってきたもの(assets/CREDITS.md)。ブラウザの決まりで、最初に画面を触るまでは鳴らせない。

export function createSound() {
  let ctx = null;
  let master = null;
  let muted = false;
  const buffers = new Map();
  const last = new Map();
  const voices = new Map(); // 音の名前 → 鳴っている数
  const bgm = new Audio(config.sound.bgm);
  bgm.loop = true;
  bgm.volume = config.sound.bgmVolume;
  // 省電力・タブを隠したときに音を止める理由(src/main.js)。どれかが残っている間は止めたまま
  const holds = new Set();
  let bgmWasPlaying = false;

  async function unlock() {
    if (ctx) return;
    ctx = new (window.AudioContext || window.webkitAudioContext)();
    master = ctx.createGain();
    master.gain.value = muted ? 0 : config.sound.masterVolume;
    master.connect(ctx.destination);
    await Promise.all(
      Object.entries(config.sound.files).map(async ([name, url]) => {
        try {
          const res = await fetch(url);
          buffers.set(name, await ctx.decodeAudioData(await res.arrayBuffer()));
        } catch {
          // 読めなかった音は鳴らさないだけ
        }
      })
    );
  }

  function play(name, { volume = 1, rate = 1 } = {}) {
    if (!ctx || muted) return;
    const buf = buffers.get(name);
    if (!buf) return;
    const limit = config.sound.limits[name];
    const now = performance.now();
    if (now - (last.get(name) || 0) < (limit?.minIntervalMs ?? config.sound.minIntervalMs)) return; // 連打を間引く
    if (limit?.maxVoices && (voices.get(name) || 0) >= limit.maxVoices) return; // 重なりすぎを防ぐ
    last.set(name, now);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = rate;
    const g = ctx.createGain();
    g.gain.value = volume;
    src.connect(g).connect(master);
    if (limit?.maxVoices) {
      voices.set(name, (voices.get(name) || 0) + 1);
      src.onended = () => voices.set(name, voices.get(name) - 1);
    }
    src.start();
  }

  function onEvents(events) {
    for (const e of events) {
      if (e.k === "kill") play(e.big ? "bigExplosion" : "explosion", { volume: e.big ? 1 : 0.35, rate: 0.9 + Math.random() * 0.25 });
      else if (e.k === "fire" && e.w === "beam") play("beam", { volume: 0.18, rate: 1.1 });
      else if (e.k === "fire" && e.w === "spread") play("beam", { volume: 0.2, rate: 1.4 });
      else if (e.k === "mainGun") play("mainGun", { volume: 0.6 });
      else if (e.k === "saberHit") play("saber", { volume: 0.25, rate: 1.2 });
      else if (e.k === "hurt") play("hit", { volume: 0.7 });
      else if (e.k === "heal") play("levelUp", { volume: 0.6, rate: 1.4 });
      else if (e.k === "pick") play("pick", { volume: 0.3, rate: 0.97 + Math.random() * 0.08 });
      else if (e.k === "levelUp") play("levelUp", { volume: 0.7 });
      else if (e.k === "slowStart") play("slowStart", { volume: 0.5 });
      else if (e.k === "slowEnd") play("slowEnd", { volume: 0.4 });
      else if (e.k === "boss") play("boss", { volume: 0.9 });
      else if (e.k === "cannonCharge") play("slowStart", { volume: 0.6, rate: 0.7 }); // 大物のチャージ主砲(ため始め)
      else if (e.k === "cannonFire") play("mainGun", { volume: 0.9, rate: 0.6 });
      else if (e.k === "dead" || e.k === "clear") play("summary", { volume: 0.7 });
    }
  }

  return {
    unlock,
    play,
    onEvents,
    startBgm() {
      if (holds.size) bgmWasPlaying = true;
      else if (!muted) bgm.play().catch(() => {});
    },
    stopBgm() {
      bgmWasPlaying = false;
      bgm.pause();
    },
    // 音を止める(reason: "idle" | "hidden")。BGM が流れていたら覚えておき、release で戻す
    hold(reason) {
      if (!holds.size) {
        bgmWasPlaying = !bgm.paused;
        bgm.pause();
        ctx?.suspend?.().catch(() => {});
      }
      holds.add(reason);
    },
    release(reason) {
      if (!holds.delete(reason) || holds.size) return;
      ctx?.resume?.().catch(() => {});
      if (bgmWasPlaying && !muted) bgm.play().catch(() => {});
    },
    setMuted(m) {
      muted = m;
      if (master) master.gain.value = m ? 0 : config.sound.masterVolume;
      if (m) bgm.pause();
    },
    get muted() {
      return muted;
    },
  };
}
